// Globals
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch } from '@/store';
import { getProductsImportTemplate, importProducts } from '../../feature';

// Components
import { StyledPageTitle, StyledBackButton, StyledChevronIcon } from '../../components/Styled';
import UploadStepContainer from '../../components/UploadStep/UploadStepContainer';
import PreparingStepContainer from '../../components/PreparingStep/PreparingStepContainer';
import { StyledHeaderWrap } from '../../components/PreparingStep/PreparingStepStyles';
import { showErrorToast, showSuccessToast, useForm } from '@/components';

// Modules
import { getPartnersList, getPartnersOptions, usePartnersListSearch } from '@/modules/Partners';
import { getCategoriesAutocomplete, useCategoriesAutocomplete } from '@/modules/Categories';
import { Resource, usePermissions } from '@/modules/Permissions';

// Models
import { IApiError } from '@/models/apiError.model';

// Helpers
import { useBoolean, useSteps } from '@/hooks';
import { ImportProductsFieldNames, TImportProductsForm, getCategoriesOptions } from '../../helpers';

const ImportContainer = () => {
  const { t } = useTranslation(['products']);
  const [form] = useForm();
  const dispatch = useAppDispatch();
  const { currentStep, prevStep, nextStep } = useSteps(2);
  const [selectedFile, setSelectedFile] = useState<File | undefined>();
  const [formValues, setFormValues] = useState<TImportProductsForm | undefined>();
  const { value: withPhotos, setValue: setWithPhotos } = useBoolean();
  const { value: success, setTrue: setSuccess } = useBoolean();
  const { list: categoriesList, onSearch: onCategoriesSearch } = useCategoriesAutocomplete({ hasChildren: '0' });
  const { partnersList, searchPartners } = usePartnersListSearch();
  const { canManage } = usePermissions();

  const canManageImport = canManage(Resource.IMPORT_PRODUCT);

  const onFileUpload = (file: File | Blob) => {
    setSelectedFile(file as File);
  };
  const resetFile = () => {
    setSelectedFile(undefined);
  };

  const onPreparationStepSubmit = () => {
    const values = form.getFieldsValue();
    setFormValues(values);

    nextStep();
  };

  const onUploadStepSubmit = async () => {
    if (!formValues) {
      return showErrorToast({ message: t('products_import_choose_category_and_partner') });
    }
    await dispatch(
      importProducts({
        with_photos: withPhotos ? '1' : '0',
        file: selectedFile,
        partner_id: formValues[ImportProductsFieldNames.PARTNER_ID],
        category_id: formValues[ImportProductsFieldNames.CATEGORY_ID],
      }),
    ).unwrap();
    showSuccessToast({ message: t('products_import_saved_success') });
    setSuccess();
  };

  const onGetTemplate = async (filled = false) => {
    const categoryId = form.getFieldValue(ImportProductsFieldNames.CATEGORY_ID);
    const partnerId = form.getFieldValue(ImportProductsFieldNames.PARTNER_ID);
    if (!categoryId) {
      return showErrorToast({ message: t('products_import_choose_category') });
    }
    if (!partnerId) {
      return showErrorToast({ message: t('products_import_choose_partner') });
    }

    try {
      const { data } = await dispatch(
        getProductsImportTemplate({
          category_id: categoryId,
          partner_id: partnerId,
          filled: filled ? '1' : '0',
        }),
      ).unwrap();
      window.open(data?.url, '_blank');
    } catch (e) {
      showErrorToast({ message: (e as unknown as IApiError).message });
    }
  };

  const onGetEmptyTemplate = async () => {
    await onGetTemplate(false);
  };

  const onGetPrefilledTemplate = async () => {
    await onGetTemplate(true);
  };

  const stepsConfig: Record<number, React.ReactNode> = {
    1: (
      <PreparingStepContainer
        onSubmit={onPreparationStepSubmit}
        onCategoriesSearch={onCategoriesSearch}
        onPartnerSearch={searchPartners}
        onGetEmptyTemplate={onGetEmptyTemplate}
        onGetPrefilledTemplate={onGetPrefilledTemplate}
        formInstance={form}
        categoryOptions={getCategoriesOptions(categoriesList ?? [])}
        partnersOptions={getPartnersOptions(partnersList ?? [])}
        canManage={canManageImport}
      />
    ),
    2: (
      <UploadStepContainer
        onFileUpload={onFileUpload}
        onUploadStepSubmit={onUploadStepSubmit}
        resetFile={resetFile}
        setWithPhotos={setWithPhotos}
        selectedFile={selectedFile}
        success={success}
        canManage={canManageImport}
      />
    ),
  };

  useEffect(() => {
    dispatch(getCategoriesAutocomplete({ name: '', limit: 500 }));
    dispatch(getPartnersList({ limit: 10 }));
  }, []);

  return (
    <>
      <StyledHeaderWrap>
        {currentStep !== 1 && (
          <StyledBackButton onClick={prevStep}>
            <StyledChevronIcon />
            {t('products_import_back')}
          </StyledBackButton>
        )}
        <StyledPageTitle>{t('products_import_upload_title')}</StyledPageTitle>
      </StyledHeaderWrap>
      {stepsConfig[currentStep]}
    </>
  );
};

export default ImportContainer;
